import { createInertiaApp } from "@inertiajs/react";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { HelmetProvider } from "react-helmet-async";
import { cosmicTheme } from "../components/ui/theme";
import { UserProvider } from "../contexts/UserContext";
import "./application.css";

type ResolvedComponent = {
  default: React.ComponentType<any>;
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const el = document.getElementById("app");

if (!el) throw new Error("❌ Root element #app not found");
if (!el.dataset.page) throw new Error("❌ Missing data-page attribute");
const page = JSON.parse(el.dataset.page);

createInertiaApp({
  // Format the title when a page sets one via <Head title="…">. Otherwise fall
  // back to document.title — which is either the server-rendered <%= meta_title %>
  // or a Helmet-managed title (PageSEO / BlogSEO on marketing + blog pages).
  //
  // A literal "VidHive" fallback here stomps on both, replacing every page's
  // <title> with the bare app name after hydration and turning every SERP /
  // AI-search citation into a 7-char "VidHive" (see issue #109).
  title: (title) => (title ? `${title} - VidHive` : document.title),

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: false,
    });
    const key = `../pages/${name}.tsx`;

    if (!pages[key])
      throw new Error(`Missing Inertia page component: '${name}.tsx'`);
    return pages[key]();
  },

  setup({ App, props }) {
    const root = createRoot(el);

    root.render(
      createElement(
        HelmetProvider, // ✅ Top-level wrapper for <Helmet>
        null,
        createElement(
          MantineProvider,
          { theme: cosmicTheme },
          createElement(
            UserProvider,
            null,
            createElement(Notifications, { position: "top-right" }),
            createElement(App, props)
          )
        )
      )
    );
  },

  progress: {
    color: "#f97316",
  },
});
